import { ItemsApi } from '@/lib/api/common/items';
import type { ItemFiltersType } from '@/lib/api/types/itemFilters';
import type { ItemType, ItemTypeLevel } from '@/lib/api/types/itemType';
import { commonQueryKeys } from '@/lib/query-keys/common';

export type ParentItemOption = {
  id: number;
  name: string;
  itemTypeId: number;
  externalKey?: string | null;
};

export const PARENT_SEARCH_LIMIT = 20;
export const PARENT_SEARCH_ORDER = 'name';
export const PARENT_SEARCH_HIERARCHICAL = false;

export function parentItemSearchQueryKey(filter: ItemFiltersType) {
  return commonQueryKeys.items.byFilters({
    filter,
    order: PARENT_SEARCH_ORDER,
    limit: PARENT_SEARCH_LIMIT,
    hierarchical: PARENT_SEARCH_HIERARCHICAL,
  });
}

export function getValidParentItemTypeIds(
  itemTypes: ItemType[],
  itemTypeLevels: ItemTypeLevel[],
  validParentLevel: number,
): number[] {
  return itemTypes
    .filter((type) => {
      const level = itemTypeLevels.find((l) => l.id === type.itemLevelId);
      return level?.level === validParentLevel;
    })
    .map((type) => type.id);
}

export function buildParentItemFilters(
  inputValue: string,
  validParentItemTypeIds: number[],
  loopId?: number,
): ItemFiltersType {
  const filters: ItemFiltersType = {};

  const trimmedInput = inputValue.trim();
  if (trimmedInput) {
    filters.name = { value: trimmedInput, operator: 'containsIgnoreCase' };
    filters.externalKey = { value: trimmedInput, operator: 'containsIgnoreCase' };
  }

  if (validParentItemTypeIds.length > 0) {
    filters.itemTypeId = { value: validParentItemTypeIds, operator: 'in' };
  }

  if (loopId !== undefined) {
    filters.loopId = { value: [loopId], operator: 'in' };
  }

  return filters;
}

export interface FetchParentItemOptionsArgs {
  inputValue: string;
  validParentItemTypeIds: number[];
  loopId?: number;
  excludedItemIds?: ReadonlySet<number>;
  limit: number;
}

export async function fetchParentItemOptions({
  inputValue,
  validParentItemTypeIds,
  loopId,
  excludedItemIds,
  limit,
}: FetchParentItemOptionsArgs): Promise<ParentItemOption[]> {
  if (validParentItemTypeIds.length === 0) return [];
  const filter = buildParentItemFilters(inputValue, validParentItemTypeIds, loopId);
  const response = await ItemsApi.getItems({
    filter,
    order: PARENT_SEARCH_ORDER,
    limit,
    hierarchical: PARENT_SEARCH_HIERARCHICAL,
  });
  return response.data
    .filter((item) => !excludedItemIds?.has(item.id))
    .map((item) => ({
      id: item.id,
      name: item.name,
      itemTypeId: item.itemTypeId,
      externalKey: item.externalKey,
    }));
}
